import posthog from 'posthog-js';

/**
 * PostHog wrapper. Mirrors the Sentry pattern: when env vars aren't
 * configured (local dev, CI, preview branches), every entry point
 * is a no-op so the app behaves identically.
 *
 * Two surfaces:
 *   - The `<AnalyticsProvider>` exported from `./analytics-provider.tsx`
 *     makes the PostHog client available via the official @posthog/react
 *     `usePostHog()` hook. Use the hook inside components when you need
 *     ad-hoc capture or feature-flag reads.
 *   - The functional `track()` / `identify()` / `trackPageView()`
 *     wrappers below are for code outside React (hooks lib, async
 *     handlers) where pulling in a hook would force a refactor.
 *
 * All event names + property shapes are documented in
 * docs/ANALYTICS.md so we don't grow a parallel undocumented schema.
 */

let initialized = false;

export function initAnalytics(appName: 'customer' | 'admin' | 'partner') {
  if (initialized) return;
  const key = (import.meta as any).env?.VITE_POSTHOG_KEY as string | undefined;
  const host = ((import.meta as any).env?.VITE_POSTHOG_HOST as string | undefined)
    // Default to the EU region — Deuce is a Dubai marketplace and
    // EU residency is closer to UAE data-protection norms than US.
    ?? 'https://eu.i.posthog.com';
  if (!key) return;

  posthog.init(key, {
    api_host: host,
    // Capture pageviews manually so we can include the logical screen
    // name (from the IA) instead of letting PostHog auto-derive from
    // window.location.pathname — that way ids in the URL don't fan
    // out into thousands of "unique pages".
    capture_pageview: false,
    capture_pageleave: true,
    // Auto-capture is off so events stay deliberate. Enable later if
    // we want broader telemetry.
    autocapture: false,
    persistence: 'localStorage+cookie',
    // Identify-then-merge so a logged-in user's pre-auth events
    // attribute to them instead of a stranded distinct_id.
    person_profiles: 'identified_only',
    // Leave replay enabled at the SDK level so Project Settings can
    // control sampling and triggers without another deploy.
    disable_session_recording: false,
    session_recording: {
      // Keep typed and pasted secrets out of replays by default.
      maskAllInputs: true,
      // Do not include request headers or bodies unless we make an
      // explicit product decision to capture them later.
      recordHeaders: false,
      recordBody: false,
    },
    loaded: (ph) => {
      // Tag every event with the app name so the same project ID
      // can host all three apps and dashboards can slice by app.
      ph.register({ app: appName });
    },
  });
  initialized = true;
}

export function identify(userId: string | null | undefined, traits?: Record<string, unknown>) {
  if (!initialized || !userId) return;
  posthog.identify(userId, traits);
}

export function resetIdentity() {
  if (!initialized) return;
  posthog.reset();
}

/** Manual pageview. Use a logical screen name, not the URL. */
export function trackPageView(screen: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.capture('$pageview', { screen, ...properties });
}

export function track(event: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.capture(event, properties);
}

/** Raw client for the rare case the wrappers don't cover. */
export { posthog };
